import React, { useEffect, useMemo, useState } from "react";
import { useCallback } from "react";
import {
  EmptyStateHeader,
  EmptyStateText,
  NotPermittedBox,
  ProfileBackground
} from "../profile/Profile.styled";
import withRefluxStore from "../../../Stores/withRefluxStore.jsx";
import {
  Anchor,
  Dashboard,
  DashboardHeader,
  TH,
  TD,
  Table,
  TableWrapper,
  customButtonStyles,
  jobBtnStyles,
  SkeletonRow,
  SkeletonCell,
  createProfileModalStyles
} from "./ProfileDashboard.styled.js";
import { useTranslation } from "react-i18next";
import CreateProfileForm from "../modals/CreateProfile.jsx";
import { PlusCircle } from "@untitledui/icons/PlusCircle";
import { CreateDomainStyles } from "../profile/requirement/options-editor/OptionsEditor.styled.js";
import {
  ActionButton,
  Modal,
  PosthogStore,
  useIsFeatureEnabled
} from "@talentbait/shared-components";
import { Group } from "../../../styles/layout";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFacetedMinMaxValues,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable
} from "@tanstack/react-table";
import ProfilesStore from "../Profile.store.js";
import Actions from "../../../Actions.js";

const fallbackTable = [
  {
    id: 1,
    title: "N/A",
    team: "N/A"
  },
  {
    id: 2,
    title: "N/A",
    team: "N/A"
  }
];

const ProfileDashboard = ({
  // store
  userProfile,
  posthogInstance,
  profiles,
  isLoadingProfiles
}) => {
  const { t } = useTranslation();
  const columnHelper = createColumnHelper();

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const hasPermission = useIsFeatureEnabled("configuration-onboarding-flag", posthogInstance);

  const handleCreateProfileClick = useCallback(() => setIsCreateModalOpen(true), []);
  const handleCreateProfileModalClose = useCallback(() => setIsCreateModalOpen(false), []);

  const handleCreateProfile = (newProfileData) => {
    const ref = Actions.createProfile.completed.listen(() => {
      setIsCreateModalOpen(false);
    });

    const refFailed = Actions.createProfile.failed.listen(() => {
      setIsCreateModalOpen(false);
    });

    Actions.createProfile(newProfileData);

    return () => {
      ref();
      refFailed();
    };
  };

  useEffect(() => {
    if (userProfile?.organizationId) {
      Actions.getProfilesByOrganizationId(userProfile.organizationId);
    }
  }, [userProfile?.organizationId]);

  const columns = useMemo(
    () => [
      columnHelper.accessor("title", {
        header: "Position",
        maxSize: 240,
        minSize: 200,
        cell: (info) => (
          <Anchor to={`/profiles/${info.row.original.id}`}>{info.getValue() || "N/A"}</Anchor>
        )
      }),
      columnHelper.accessor("not-existing-yet", {
        header: "Title",
        maxSize: 240,
        minSize: 120,
        cell: () => "N/A"
      }),
      columnHelper.accessor("team", {
        header: "Department",
        minSize: 140,
        cell: (info) => info.getValue() || "N/A"
      }),
      columnHelper.accessor("level", {
        header: "Level",
        minSize: 100,
        cell: (info) => info.getValue() || "N/A"
      }),
      columnHelper.display({
        id: "campaigns",
        header: "Campaigns",
        minSize: 120,
        meta: { isCentered: true },
        cell: () => 0
      }),
      columnHelper.display({
        id: "jobListings",
        header: "Job Listings",
        minSize: 130,
        meta: { isCentered: true },
        cell: () => 0
      }),
      columnHelper.display({
        id: "create",
        header: "Create",
        minSize: 280,
        cell: () => (
          <Group direction="row">
            <ActionButton variant="secondary" text={t("Job page")} styles={jobBtnStyles} />
            <ActionButton variant="create" text={t("Campaign")} styles={customButtonStyles} />
          </Group>
        )
      })
    ],
    [columnHelper, t]
  );

  const table = useReactTable({
    data: profiles ?? fallbackTable,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues()
  });

  if (!hasPermission) {
    return (
      <ProfileBackground>
        <NotPermittedBox>
          <EmptyStateHeader>{t("You do not have permission to view this page.")}</EmptyStateHeader>
          <EmptyStateText>
            {t("Please contact your administrator if you believe this is an error.")}
          </EmptyStateText>
        </NotPermittedBox>
      </ProfileBackground>
    );
  }

  const TableView = () => {
    if (isLoadingProfiles) {
      return Array.from({ length: 5 }).map((_, rowIdx) => (
        <SkeletonRow key={rowIdx}>
          <SkeletonCell width="240px" />
          <SkeletonCell width="120px" />
          <SkeletonCell width="140px" />
          <SkeletonCell width="100px" />
          <SkeletonCell width="120px" />
          <SkeletonCell width="130px" />
          <SkeletonCell width="280px" />
        </SkeletonRow>
      ));
    }

    if (profiles.length === 0) {
      return (
        <tr>
          <TD colSpan={columns.length} $isCentered>
            {t("No profiles found. Add a new profile to get started!")}
          </TD>
        </tr>
      );
    }

    return table.getRowModel().rows.map((row) => (
      <tr key={row.id}>
        {row.getVisibleCells().map((cell) => (
          <TD key={cell.id} $isCentered={cell.column.columnDef.meta?.isCentered}>
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </TD>
        ))}
      </tr>
    ));
  };

  return (
    <ProfileBackground>
      <Dashboard>
        <DashboardHeader>{t("Job Profiles")}</DashboardHeader>

        <TableWrapper>
          <Table>
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TH key={header.id} $isCentered={header.column.columnDef.meta?.isCentered}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </TH>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>{TableView()}</tbody>
          </Table>
        </TableWrapper>

        <Group direction="row" width="100%" justify="center">
          <ActionButton
            id="add-profile-button"
            icon={<PlusCircle size="20px" />}
            variant="link"
            text={t("Add profile")}
            onClick={handleCreateProfileClick}
            styles={CreateDomainStyles}
          />
        </Group>
      </Dashboard>

      <Modal
        isOpen={isCreateModalOpen}
        setIsOpen={setIsCreateModalOpen}
        styles={createProfileModalStyles}
      >
        <CreateProfileForm
          onSubmit={handleCreateProfile}
          onCancel={handleCreateProfileModalClose}
          organizationId={userProfile.organizationId}
        />
      </Modal>
    </ProfileBackground>
  );
};

export default withRefluxStore(
  ProfileDashboard,
  [PosthogStore, ProfilesStore],
  ["userProfile", "posthogInstance", "profiles", "isLoadingProfiles"]
);
